import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Toolbar,
  Box,
  Container,
  IconButton,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Button,
  Badge,
} from '@mui/material';
import {
  Search as SearchIcon,
  ShoppingBag as ShoppingBagIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  KeyboardArrowDown as ArrowDownIcon,
  Person as PersonIcon,
  FavoriteBorder as FavoriteIcon,
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import * as Icons from '@mui/icons-material';
import SearchModal from './SearchModal';
import TextIcon from '../../components/common/TextIcon';
import { LOGO_HEIGHTS } from '../../components/common/DynamicStyles';
import useTopBarVisible from '../hooks/useTopBarVisible';
import { categoriesApi } from '../api/categories';
import { cartApi } from '../api/cart';
import authService from '../services/authService';
import { getTemplateStyle } from '../theme/templateStyles';
import { profileApi } from '../api/profile';
import secureLocalStorage from 'react-secure-storage';

const Navbar = () => {
  const navigate = useNavigate();
  const template = useSelector((state) => state.template);
  const { businessName, businessPhone, evoucherEnabled } = useSelector((state) => state.configs);
  const topBar = template?.topBar;
  const socials = template?.socialMedia;
  const logo = template?.logo;
  // 'centered' variant floats the logo in the middle of the toolbar
  const centeredNav = getTemplateStyle(template?.template).nav === 'centered';
  const topBarVisible = useTopBarVisible();
  const LeftSectionIcon = Icons[topBar?.leftSectionIcon ?? 'AddShoppingCart'] || Icons.AddShoppingCart;
  const MiddleSectionIcon = Icons[topBar?.middleSectionIcon ?? 'Notifications'] || Icons.Notifications;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [categoryMenuAnchor, setCategoryMenuAnchor] = useState(null);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  // Redux reflects in-session login/logout; secureLocalStorage covers page refreshes
  const reduxLoggedIn = useSelector((state) => state.user?.isLoggedIn);
  const isLoggedIn = reduxLoggedIn || authService.isLoggedIn();
  const currentUser = isLoggedIn ? authService.getCurrentUser() : null;
  const userName = currentUser?.username || currentUser?.email?.split('@')[0] || 'there';
  const [cartItemCount, setCartItemCount] = useState(0);
  const [wishlistItemCount, setWishlistItemCount] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesFetched, setCategoriesFetched] = useState(false);

  const categoryMenuOpen = Boolean(categoryMenuAnchor);

  // Update cart count when component mounts and when cart changes
  useEffect(() => {
    // Get initial cart count
    const updateCartCount = () => {
      const count = cartApi.getCartItemCount();
      setCartItemCount(count);
    };

    updateCartCount();

    // Listen for cart updates
    const handleCartUpdate = () => {
      updateCartCount();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);

    // Cleanup listener
    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
    };
  }, []);

  // Update wishlist count when component mounts and when wishlist changes
  useEffect(() => {
    // Get initial wishlist count
    const updateWishlistCount = async () => {
      const customerId = secureLocalStorage.getItem('customerId');

      if (customerId) {
        // Authenticated user - fetch from API (get actual count)
        try {
          const result = await profileApi.getWishlist(customerId, { page: 1, limit: 100 });
          if (result.success) {
            // Get the actual count from the response
            const count = result.items ? result.items.length : (result.total || 0);
            setWishlistItemCount(count);
          } else {
            setWishlistItemCount(0);
          }
        } catch (error) {
          console.error('Error fetching wishlist count:', error);
          setWishlistItemCount(0);
        }
      } else {
        // Non-authenticated user - use localStorage
        const savedWishlist = localStorage.getItem('wishlist');
        const count = savedWishlist ? JSON.parse(savedWishlist).length : 0;
        setWishlistItemCount(count);
      }
    };

    updateWishlistCount();

    // Listen for wishlist updates
    const handleWishlistUpdate = () => {
      updateWishlistCount();
    };

    window.addEventListener('wishlistUpdated', handleWishlistUpdate);

    // Cleanup listener
    return () => {
      window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
    };
  }, []);

  // Fetch categories from API when hovering on Products
  const fetchCategories = async () => {
    if (categoriesFetched || loading) return;

    setLoading(true);

    try {
      const result = await categoriesApi.getAll(1000);

      if (result && result.data && Array.isArray(result.data)) {
        setCategories(result.data);
        setCategoriesFetched(true);
      } else {
        setCategories([]);
        setCategoriesFetched(true);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  const [menuLeaveTimeout, setMenuLeaveTimeout] = React.useState(null);

  const handleCategoryMenuOpen = (event) => {
    // Clear any pending close
    if (menuLeaveTimeout) {
      clearTimeout(menuLeaveTimeout);
      setMenuLeaveTimeout(null);
    }

    setCategoryMenuAnchor(event.currentTarget);
    fetchCategories(); // Fetch categories when opening the menu
  };

  const handleCategoryMenuClose = () => {
    setCategoryMenuAnchor(null);
    setHoveredCategory(null);
  };

  const handleMenuMouseLeave = (event) => {
    // Check if the mouse is moving to a related target (like the menu itself)
    if (event && event.relatedTarget) {
      const relatedTarget = event.relatedTarget;
      // If moving to the menu or something inside it, don't close
      if (relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('[id="products-menu"]')) {
        return;
      }
    }

    const timeout = setTimeout(() => {
      handleCategoryMenuClose();
    }, 150);
    setMenuLeaveTimeout(timeout);
  };

  const navigationLinks = [
    { title: 'Home', path: '/' },
    { title: 'Products', path: '#', hasDropdown: true },
    ...(evoucherEnabled ? [{ title: 'E-Voucher', path: '/evoucher' }] : []),
    { title: 'FAQs', path: '/faqs' },
    { title: 'Contact Us', path: '/contact' },
  ];

  const renderMobileMenu = (
    <Drawer
      anchor="right"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#ffffff',
        }
      }}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          p: 2,
          borderBottom: '1px solid #e0e0e0',
        }}>
          <Typography sx={{ fontSize: '1.2rem', fontWeight: 600, fontFamily: 'var(--body-font)' }}>
            Menu
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <CloseIcon />
          </IconButton>
        </Box>

        <List sx={{ flexGrow: 1, px: 2 }}>
          {navigationLinks.map((link) => (
            <ListItem
              key={link.path}
              onClick={() => {
                navigate(link.hasDropdown ? '/products' : link.path);
                setMobileMenuOpen(false);
              }}
              sx={{
                py: 2,
                cursor: 'pointer',
                borderBottom: '1px solid #f0f0f0',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.02)',
                }
              }}
            >
              <ListItemText
                primary={link.title}
                primaryTypographyProps={{
                  sx: {
                    fontSize: '1rem',
                    fontWeight: 500,
                    fontFamily: 'var(--body-font)',
                  }
                }}
              />
            </ListItem>
          ))}

          {/* Phone Number */}
          {businessPhone && (
            <ListItem
              sx={{
                py: 2,
                borderTop: '2px solid #f0f0f0',
                mt: 2,
              }}
            >
              <PhoneIcon sx={{ mr: 2, color: 'primary.main' }} />
              <ListItemText
                primary={businessPhone}
                secondary="Call for assistance"
                primaryTypographyProps={{
                  sx: {
                    fontSize: '1rem',
                    fontWeight: 600,
                    fontFamily: 'var(--body-font)',
                    color: 'primary.main',
                  }
                }}
                secondaryTypographyProps={{
                  sx: {
                    fontFamily: 'var(--body-font)',
                  }
                }}
              />
            </ListItem>
          )}
        </List>
      </Box>
    </Drawer>
  );

  const hasLeftSectionText = Boolean(topBar?.leftSectionText && topBar.leftSectionText.trim());
  const hasMiddleSectionText = Boolean(topBar?.middleSectionText && topBar.middleSectionText.trim());

  return (
    <>
      {/* Configurable top bar */}
      {topBarVisible && (
        <Box sx={{
          backgroundColor: 'var(--primary)',
          py: 0.5,
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
        }}>
          {hasLeftSectionText && (
            <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
              <LeftSectionIcon sx={{ color: '#ffffff', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600 }}>
                {topBar?.leftSectionText}
              </Typography>
            </Box>
          )}
          {hasMiddleSectionText && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mx: 'auto' }}>
              <MiddleSectionIcon sx={{ color: '#ffffff', fontSize: 18 }} />
              <Typography variant="caption" sx={{ color: '#ffffff', fontWeight: 600, letterSpacing: '0.05em' }}>
                {topBar?.middleSectionText}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
            {socials?.instagram && (
              <IconButton component="a" href={socials.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" size="small" sx={{ color: '#ffffff', ml: 0.5, p: 0.25 }}>
                <Icons.Instagram sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {socials?.facebook && (
              <IconButton component="a" href={socials.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" size="small" sx={{ color: '#ffffff', ml: 0.5, p: 0.25 }}>
                <Icons.Facebook sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {socials?.x && (
              <IconButton component="a" href={socials.x} target="_blank" rel="noreferrer" aria-label="X (Twitter)" size="small" sx={{ color: '#ffffff', ml: 0.5, p: 0.25 }}>
                <Icons.Twitter sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {socials?.linkedIn && (
              <IconButton component="a" href={socials.linkedIn} target="_blank" rel="noreferrer" aria-label="LinkedIn" size="small" sx={{ color: '#ffffff', ml: 0.5, p: 0.25 }}>
                <Icons.LinkedIn sx={{ fontSize: 18 }} />
              </IconButton>
            )}
            {socials?.tikTok && (
              <IconButton component="a" href={socials.tikTok} target="_blank" rel="noreferrer" aria-label="TikTok" size="small" sx={{ color: '#ffffff', ml: 0.5, p: 0.25 }}>
                <Icons.MusicNote sx={{ fontSize: 18 }} />
              </IconButton>
            )}
          </Box>
        </Box>
      )}

      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        <Container maxWidth={false}>
          <Toolbar sx={{ minHeight: { xs: 80, md: 100 }, px: { xs: 1, md: 3 }, position: 'relative' }}>
            {/* Logo */}
            <Box
              component={Link}
              to="/"
              sx={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                mr: { xs: 2, md: 6 },
                ...(centeredNav && {
                  position: { md: 'absolute' },
                  left: { md: '50%' },
                  transform: { md: 'translateX(-50%)' },
                  mr: { xs: 2, md: 0 },
                  zIndex: 1,
                }),
              }}
            >
              {logo?.logoType === 'logo' && logo?.logoPath ? (
                <Box
                  component="img"
                  src={logo.logoPath}
                  alt={`${logo?.logoText || 'Store'} logo`}
                  sx={{ height: { xs: '55px', md: 'var(--navbar-logo-height, 70px)' }, width: 'auto', maxWidth: { xs: '140px', md: '220px' }, objectFit: 'contain' }}
                />
              ) : (
                <TextIcon text={logo?.logoText || businessName?.charAt(0)?.toUpperCase() || 'A'} width={LOGO_HEIGHTS[logo?.size] || 70} height={LOGO_HEIGHTS[logo?.size] || 70} />
              )}
            </Box>

            {/* Desktop Navigation */}
            <Box sx={{
              flexGrow: 1,
              display: { xs: 'none', md: 'flex' },
              justifyContent: centeredNav ? 'flex-start' : 'center',
              alignItems: 'center',
              gap: { md: 4, lg: 6 },
              px: 2,
            }}>
              {navigationLinks.map((link) => (
                link.hasDropdown ? (
                  <Box
                    key={link.title}
                    onMouseEnter={handleCategoryMenuOpen}
                    onMouseLeave={handleMenuMouseLeave}
                  >
                    <Button
                      id="products-button"
                      aria-controls={categoryMenuOpen ? 'products-menu' : undefined}
                      aria-haspopup="true"
                      aria-expanded={categoryMenuOpen ? 'true' : undefined}
                      onClick={() => { handleCategoryMenuClose(); navigate('/products'); }}
                      sx={{
                        color: '#000000',
                        fontSize: '0.95rem',
                        fontWeight: 500,
                        fontFamily: 'var(--body-font)',
                        textTransform: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        '&:hover': {
                          backgroundColor: 'transparent',
                          color: 'var(--primary)',
                        }
                      }}
                      endIcon={<ArrowDownIcon />}
                    >
                      {link.title}
                    </Button>
                  </Box>
                ) : (
                  <Typography
                    key={link.path}
                    component={Link}
                    to={link.path}
                    sx={{
                      color: '#000000',
                      textDecoration: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      fontFamily: 'var(--body-font)',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'color 0.2s',
                      '&:hover': {
                        color: 'var(--primary)',
                      }
                    }}
                  >
                    {link.title}
                  </Typography>
                )
              ))}
            </Box>

            {/* Phone Number - Desktop */}
            {businessPhone && (
              <Box sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                mr: 3,
                borderLeft: '1px solid #e0e0e0',
                pl: 3,
              }}>
                <PhoneIcon sx={{ fontSize: 20, mr: 0.5, color: 'var(--primary)', verticalAlign: 'middle' }} />
                <Typography
                  component="a"
                  href={`tel:${businessPhone}`}
                  sx={{
                    color: '#000000',
                    textDecoration: 'none',
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    fontFamily: 'var(--body-font)',
                    lineHeight: 1.2,
                    display: 'flex',
                    alignItems: 'center',
                    '&:hover': {
                      color: 'var(--primary)',
                    }
                  }}
                >
                  {businessPhone}
                </Typography>
              </Box>
            )}

            {/* Right Icons */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <IconButton
                onClick={() => setSearchOpen(true)}
                sx={{ color: '#000000' }}
              >
                <SearchIcon />
              </IconButton>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {isLoggedIn && (
                  <Typography
                    sx={{
                      mr: 1,
                      fontSize: '0.9rem',
                      fontFamily: 'var(--body-font)',
                      display: { xs: 'none', sm: 'block' },
                    }}
                  >
                    Hi, {userName}!
                  </Typography>
                )}
                {!isLoggedIn && (
                  <Typography
                    sx={{
                      mr: 1,
                      fontSize: '0.9rem',
                      fontFamily: 'var(--body-font)',
                      display: { xs: 'none', sm: 'block' },
                      cursor: 'pointer',
                      '&:hover': { color: 'var(--primary)' },
                    }}
                    onClick={() => navigate('/account', { state: { register: true } })}
                  >
                    Sign up
                  </Typography>
                )}
                <IconButton
                  onClick={() => navigate('/account')}
                  sx={{ color: '#000000' }}
                >
                  <PersonIcon />
                </IconButton>
              </Box>

              <IconButton
                onClick={() => navigate('/wishlist')}
                sx={{ color: '#000000' }}
              >
                <Badge
                  badgeContent={wishlistItemCount}
                  color="error"
                  invisible={wishlistItemCount === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: '#dc2626',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.65rem',
                      height: '20px',
                      minWidth: '20px',
                      borderRadius: '50%',
                    },
                  }}
                >
                  <FavoriteIcon />
                </Badge>
              </IconButton>

              <IconButton
                onClick={() => navigate('/cart')}
                sx={{ color: '#000000' }}
              >
                <Badge
                  badgeContent={cartItemCount}
                  color="error"
                  invisible={cartItemCount === 0}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: 'var(--primary)',
                      color: 'white',
                      fontWeight: 600,
                      fontSize: '0.7rem',
                      minWidth: '18px',
                      height: '18px',
                    }
                  }}
                >
                  <ShoppingBagIcon />
                </Badge>
              </IconButton>

              <IconButton
                onClick={() => setMobileMenuOpen(true)}
                sx={{
                  color: '#000000',
                  display: { xs: 'flex', md: 'none' }
                }}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* Mega Menu for Categories - Using custom Box instead of Menu */}
      {categoryMenuOpen && (
        <Box
          id="products-menu"
          onMouseEnter={() => {
            // Clear any pending close when entering menu
            if (menuLeaveTimeout) {
              clearTimeout(menuLeaveTimeout);
              setMenuLeaveTimeout(null);
            }
          }}
          onMouseLeave={(event) => {
            // Check if moving back to the button
            if (event && event.relatedTarget) {
              const relatedTarget = event.relatedTarget;
              if (relatedTarget && typeof relatedTarget.closest === 'function' && relatedTarget.closest('[id="products-button"]')) {
                return;
              }
            }

            const timeout = setTimeout(() => {
              handleCategoryMenuClose();
            }, 150);
            setMenuLeaveTimeout(timeout);
          }}
          sx={{
            position: 'fixed',
            top: categoryMenuAnchor ? categoryMenuAnchor.getBoundingClientRect().bottom : 0,
            left: categoryMenuAnchor ? categoryMenuAnchor.getBoundingClientRect().left : 0,
            width: '100%',
            maxWidth: '1200px',
            backgroundColor: '#ffffff',
            borderRadius: 0,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
            zIndex: 1200,
            animation: `fadeIn 0.2s ease-in`,
            '@keyframes fadeIn': {
              from: { opacity: 0, transform: 'translateY(-10px)' },
              to: { opacity: 1, transform: 'translateY(0)' }
            }
          }}
        >
          <Container sx={{ py: 3 }}>
            {loading ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ fontFamily: 'var(--body-font)' }}>Loading categories...</Typography>
              </Box>
            ) : categories.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography sx={{ fontFamily: 'var(--body-font)', mb: 1 }}>No categories available</Typography>
                <Typography
                  component={Link}
                  to="/products"
                  onClick={handleCategoryMenuClose}
                  sx={{ fontFamily: 'var(--body-font)', color: 'var(--primary)', fontWeight: 600, textDecoration: 'none' }}
                >
                  Browse all products →
                </Typography>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {categories
                  .map((category) => (
                    <Box key={category.id} sx={{ minWidth: '200px' }}>
                      <Typography
                        component={Link}
                        to={`/products?category=${encodeURIComponent(category.categoryName)}`}
                        onClick={handleCategoryMenuClose}
                        sx={{
                          display: 'block',
                          fontSize: '0.8rem',
                          fontWeight: 700,
                          fontFamily: 'var(--body-font)',
                          letterSpacing: '0.1em',
                          color: '#666',
                          mb: 2,
                          textTransform: 'uppercase',
                          textDecoration: 'none',
                          cursor: 'pointer',
                          '&:hover': {
                            color: 'var(--primary)',
                          }
                        }}
                        onMouseEnter={() => setHoveredCategory(category)}
                      >
                        {category.categoryName}
                      </Typography>

                      {/* Subcategories */}
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {(category.subcategories || []).map((subcategory) => (
                          <Typography
                            key={subcategory.id}
                            component={Link}
                            to={`/products?category=${encodeURIComponent(category.categoryName)}&subcategory=${encodeURIComponent(subcategory.name)}`}
                            onClick={handleCategoryMenuClose}
                            sx={{
                              fontSize: '0.9rem',
                              fontFamily: 'var(--body-font)',
                              color: subcategory.isFeatured ? 'var(--primary)' : '#000',
                              fontWeight: subcategory.isFeatured ? 600 : 400,
                              textDecoration: 'none',
                              py: 0.5,
                              px: 1,
                              borderRadius: '4px',
                              transition: 'all 0.2s',
                              backgroundColor: hoveredCategory?.id === category.id ? '#f5f5f5' : 'transparent',
                              '&:hover': {
                                backgroundColor: '#f5f5f5',
                                color: 'var(--primary)',
                                transform: 'translateX(5px)',
                              }
                            }}
                          >
                            {subcategory.name}
                            {subcategory.isFeatured && (
                              <Box
                                component="span"
                                sx={{
                                  ml: 1,
                                  fontSize: '0.7rem',
                                  fontFamily: 'var(--body-font)',
                                  backgroundColor: 'var(--primary)',
                                  color: 'white',
                                  px: 0.8,
                                  py: 0.2,
                                  borderRadius: '12px',
                                  fontWeight: 600,
                                }}
                              >
                                HOT
                              </Box>
                            )}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                <Box sx={{ width: '100%', pt: 2, borderTop: '1px solid #eee' }}>
                  <Typography
                    component={Link}
                    to="/products"
                    onClick={handleCategoryMenuClose}
                    sx={{ fontFamily: 'var(--body-font)', color: 'var(--primary)', fontWeight: 600, fontSize: '0.9rem', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                  >
                    View All Products →
                  </Typography>
                </Box>
              </Box>
            )}
          </Container>
        </Box>
      )}

      {renderMobileMenu}

      <SearchModal open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
};

export default Navbar;
